import { escapeHtml } from './dom.js';

/**
 * A control with its label notched into its own top border.
 *
 * The split table is a grid of controls that each need naming, and the
 * ordinary answer — a label on the line above — only works when every column
 * has one. Give three columns a label and leave two without and the row is a
 * staircase: the named controls sit a line lower than the unnamed ones, and
 * the eye has to find the baseline again in every row. Putting the label in
 * the border gives every column its name and keeps every control on one line.
 *
 * A real `<fieldset>` and `<legend>`, because that is the one element pair
 * browsers already lay out this way: the legend genuinely interrupts the
 * border rather than being painted over it. The obvious alternative — a
 * label with the surface's background colour sitting on top of the border —
 * needs the colour behind it to be known, and here it is not: a split row
 * can be hovered, or hatched while a change is pending, and either would
 * show as a seam straight through the label.
 *
 * `role="none"`, because the grouping a fieldset implies is a lie here:
 * there is one control inside, not a group of related ones, and a screen
 * reader announcing "Account group, Account" is noise. The legend is
 * therefore decoration, and **the control must carry its own `aria-label`**
 * — the accessible name has to come from somewhere.
 */
export function notchedFieldHtml(label: string, control: string, labelAttr = ''): string {
  return `<fieldset class="wl-nf" role="none">`
    + `<legend${labelAttr ? ` ${labelAttr}` : ''}>${escapeHtml(label)}</legend>`
    + `${control}</fieldset>`;
}

/**
 * The one way a filter says "and here is how to turn me off".
 *
 * The wallet log grew four filters and four idioms: a select cleared by
 * choosing its first option, a tree cleared by a text link, a second select
 * cleared by its first option again, and a tag popover cleared by another
 * link. Four ways to do one thing, none of them findable from the others.
 *
 * So: one button, in the same place in every one of them — the right-hand
 * end of the control, inside its own border, where a browser puts the clear
 * affordance on a search field. Hidden rather than removed when there is
 * nothing to clear, so the control does not change width as it is used.
 *
 * `×` is drawn, not typed: the multiplication sign renders at a different
 * weight in every font this runs under, and beside a 0.82rem control it
 * looked like a stray letter in two of them.
 *
 * Optional by construction. The account control is the same control on the
 * forms, where there is no "no account" to clear to — so the caller decides,
 * and a field that cannot be cleared simply does not ask for one.
 */
export function fieldClearHtml(id: string, says: string): string {
  return `<button type="button" class="wl-nf__clear" id="${escapeHtml(id)}" hidden`
    + ` title="${escapeHtml(says)}" aria-label="${escapeHtml(says)}">`
    + '<svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true" focusable="false">'
    + '<path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" stroke-width="2"'
    + ' stroke-linecap="round" fill="none"/></svg></button>';
}

/**
 * A notched field whose control sits beside its clear button.
 *
 * The pair is a flex row inside the notch, so the `×` keeps to the right
 * whatever the control is — a `<select>`, a button that opens a tree, or a
 * button that opens a popover. A `<select>` draws its own caret at its right
 * edge, so the button follows it rather than covering it.
 */
export function clearableFieldHtml(
  label: string, control: string, clear: string, labelAttr = '',
): string {
  return notchedFieldHtml(label, `<div class="wl-nf__clearable">${control}${clear}</div>`, labelAttr);
}
